import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../screens/firebaseConfig';
import { useT } from '../i18n/LanguageContext';

// Google's "G", inline so the button needs no image request (and no brand asset in /public).
const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.7 17.8 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z" />
    <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.7-6c-2.2 1.5-5 2.3-8.2 2.3-6.2 0-11.5-4.2-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
  </svg>
);

/**
 * "Continue with Google". Free to run (unlike SMS codes, which Firebase bills per text) and the
 * account most Android phones already have. Works for sign-in and sign-up alike: a first-time Google
 * user comes back from Firebase signed in, and the app sends them through onboarding.
 */
export default function GoogleButton({ onDone, onError, disabled }) {
  const t = useT();
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      onDone?.();
    } catch (error) {
      const code = error?.code || '';
      // Closing the Google window is a choice, not an error.
      if (!code.includes('popup-closed-by-user') && !code.includes('cancelled-popup-request')) {
        onError?.(code.includes('operation-not-allowed') ? t('auth.googleDisabled') : t('auth.googleFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      fullWidth
      variant="outlined"
      onClick={go}
      disabled={disabled || busy}
      startIcon={busy ? <CircularProgress size={16} /> : <GoogleG />}
      sx={{
        height: 44, borderRadius: 2, textTransform: 'none', fontSize: 14, fontWeight: 600,
        color: '#17202A', borderColor: '#D7E2EC', bgcolor: '#fff',
        '&:hover': { borderColor: '#185FA5', bgcolor: '#F6F8FB' },
      }}
    >
      {t('auth.google')}
    </Button>
  );
}
